import { Controller } from "@hotwired/stimulus"
import { writeClipboard } from "../utils/clipboard"
import { htmlToMarkdown } from "../utils/html_to_markdown"

/**
 * Copy Widget Controller
 *
 * Automatically transforms divs with data-widget="copy" into copyable elements
 * with a copy button. The button copies rich HTML to the clipboard with
 * Markdown as the plain-text fallback, so pasting works in both rich editors
 * and plain-text fields.
 *
 * Usage:
 * <div data-widget="copy">
 *   CONTENT TO COPY
 * </div>
 */
export default class extends Controller {
  static targets = [ "source" ]

  connect() {
    this._transformHandler = () => this.transformWidget()
    document.addEventListener("turbo:load", this._transformHandler)
    document.addEventListener("DOMContentLoaded", this._transformHandler)

    if (document.readyState !== 'loading') {
      this._transformTimer = setTimeout(this._transformHandler, 100)
    }
  }

  disconnect() {
    if (this._transformHandler) {
      document.removeEventListener("turbo:load", this._transformHandler)
      document.removeEventListener("DOMContentLoaded", this._transformHandler)
    }
    if (this._transformTimer) {
      clearTimeout(this._transformTimer)
      this._transformTimer = null
    }
  }

  transformWidget() {
    // This runs from a deferred setTimeout, which can fire after the document
    // is gone (page navigation, or test environment teardown). Bail out safely.
    if (typeof document === "undefined") return

    const copyWidgets = document.querySelectorAll('[data-widget="copy"]:not([data-copy-widget-transformed])')
    copyWidgets.forEach(widget => this.transformElement(widget))
  }

  manualTransform() {
    this.transformWidget()
  }

  copy(event) {
    event.preventDefault()
    event.stopPropagation()
    const container = event.target.closest('[data-controller*="copy-widget"]')
    const contentArea = container.querySelector('[data-clipboard-target="source"]')

    writeClipboard({
      html: contentArea.innerHTML,
      text: htmlToMarkdown(contentArea)
    }).then(() => this.showCopyFeedback(container, I18n.copy_widget.copied))
  }

  showCopyFeedback(container, message) {
    const button = container.querySelector('[role="tooltip"]')
    if (!button) return

    if (!button.dataset.originalLabel) {
      button.dataset.originalLabel = button.getAttribute("aria-label")
      button.addEventListener("mouseleave", () => {
        button.setAttribute("aria-label", button.dataset.originalLabel)
        delete button.dataset.originalLabel
      }, { once: true })
    }
    button.setAttribute("aria-label", message)
  }

  transformElement(element) {
    element.setAttribute('data-copy-widget-transformed', 'true')

    const originalClasses = element.className

    const wrapper = document.createElement('div')
    wrapper.className = `copy-widget-container flex items-start gap-4 justify-between ${originalClasses}`
    wrapper.setAttribute('data-controller', 'copy-widget')

    const contentArea = document.createElement('div')
    contentArea.className = 'copy-widget-content flex-1'
    contentArea.setAttribute('data-clipboard-target', 'source')

    while (element.firstChild) {
      contentArea.appendChild(element.firstChild)
    }

    const copyButton = document.createElement('button')
    copyButton.setAttribute('aria-label', I18n.copy_widget.copy)
    copyButton.setAttribute('role', 'tooltip')
    copyButton.setAttribute('data-microtip-position', 'top')
    copyButton.setAttribute('data-action', 'click->copy-widget#copy')
    copyButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="icon-svg text-gray-400 w-5 hover:text-blue hover:scale-110 transition-transform cursor-pointer">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"/>
      </svg>
    `

    wrapper.appendChild(contentArea)
    wrapper.appendChild(copyButton)

    element.parentNode.replaceChild(wrapper, element)
  }
}
